import {
  isTypeAutomated_content_tile,
  isTypeTile,
  TypeItems_containerWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { BodyProps, Container, HeadingProps } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { AutomatedContentTile } from "../AutomatedContent";
import { Tile } from "../Tile";
import { TileProps } from "../Tile/Tile.types";
// @ts-ignore
import styles from "./ItemsContainer.module.scss";

const spacingMap: Record<string, string> = {
  "Large (24/16)": "large",
  "Medium (16/12)": "medium",
  "Small (8/4)": "small",
  "None (0/0)": "none",
};

interface ItemsContainerProps {
  orientation: "horizontal" | "vertical";
  layoutOrientation?: "grid" | "stacked";
  stackedLayoutPadding?: "Large (24/16)" | "Medium (16/12)" | "None (0/0)" | "Small (8/4)";
  className?: string;
  heading?: Pick<HeadingProps, "variant">;
  body?: Pick<BodyProps<"p">, "variant">;
  entry: TypeItems_containerWithoutUnresolvableLinksResponse;
  phoneNumber?: string;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--tile-background-color"?: string;
  "--divider-line-color"?: string;
}

export const ItemsContainer: React.FC<ItemsContainerProps> = ({
  orientation,
  layoutOrientation = "grid",
  stackedLayoutPadding = "Large (24/16)",
  className,
  entry,
  heading,
  body,
  phoneNumber,
}) => {
  const style: CustomCSSProperties = {};

  const { show_divider_line, divider_line_color, items_background_color } = entry?.fields || {};

  if (items_background_color) style["--tile-background-color"] = `var(${items_background_color})`;

  if (show_divider_line) style["--divider-line-color"] = `var(${divider_line_color})`;

  const mobileTextOrientation = entry?.fields?.mobile_item_orientation || orientation;
  const desktopTextOrientation = entry?.fields?.desktop_item_orientation || orientation;
  const mobileLayoutOrientation = entry?.fields?.mobile_layout_orientation || layoutOrientation;
  const desktopLayoutOrientation = entry?.fields?.desktop_layout_orientation || layoutOrientation;
  const selectedStackedPadding = entry?.fields?.stacked_layout_padding || stackedLayoutPadding;

  const items = entry?.fields?.items || [];
  const badge = items?.some((item) => item && isTypeTile(item) && item?.fields?.variation === "badge");
  const divider = items?.some((item) => item && isTypeTile(item) && item?.fields?.variation === "divider");

  const tileCfg = {
    padded: Boolean(items_background_color),
    desktopOrientation: desktopTextOrientation,
    mobileOrientation: mobileTextOrientation,
    desktopLayoutOrientation: desktopLayoutOrientation,
    mobileLayoutOrientation: mobileLayoutOrientation,
    stackedLayoutPadding: selectedStackedPadding,
  } satisfies Partial<TileProps>;

  return (
    <Container
      flex
      wrap="wrap"
      justifyContent="center"
      lg={{ justifyContent: orientation === "vertical" ? "center" : "flex-start" }}
      className={cn(styles.itemsContainer, className, {
        [styles.badgeWrapper]: badge,
        [styles.dividerWrapper]: divider,
        [styles.hasDividerLine]: show_divider_line && items.length === 2,
        [styles[`spacing-mobile-${spacingMap[selectedStackedPadding]}`]]: mobileLayoutOrientation === "stacked",
        [styles[`spacing-desktop-${spacingMap[selectedStackedPadding]}`]]: desktopLayoutOrientation === "stacked",
      })}
      style={style}
    >
      {items.map((item, index) => {
        if (!item) return null;

        const tileStyles = cn(styles.tile, {
          [styles.tileDesktop]: orientation === "vertical",
          [styles.stackedMobile]: mobileLayoutOrientation === "stacked",
          [styles.stackedDesktop]: desktopLayoutOrientation === "stacked",
        });

        if (isTypeTile(item)) {
          return (
            <Tile
              key={index}
              variation={item?.fields?.variation}
              {...tileCfg}
              content={item}
              heading={heading}
              body={body}
              phoneNumber={phoneNumber}
                className={cn(tileStyles, {
                [styles.badge]: item?.fields?.variation === "badge",
                [styles.divider]: item?.fields?.variation === "divider",
              })}
            />
          );
        }
        if (isTypeAutomated_content_tile(item)) {
          return (
            <AutomatedContentTile
              key={index}
              {...item.fields}
              className={tileStyles}
              tileConfig={{
                ...tileCfg,
                variationStyles: {
                  badge: styles.badge,
                  divider: styles.divider,
                },
              }}
              phoneNumber={phoneNumber}
            />
          );
        }
        return null;
      })}
    </Container>
  );
};
